import type { ArtSpec } from '../sim/level';
import { ART_PICTURE_NAMES, pictureOnCanvas, type ArtPicture } from './artPictures';
import { toAsciiRows } from '../content/asciiMap';
import { allowedMowers, loadLevel, type LevelFile } from '../content/levelSchema';
import { MOWER_DEFS, MOWERS, type MowerId } from '../content/mowers';
import type { TileId } from '../content/tiles';
import { DIRECTIONS, dx, dy } from '../sim/direction';
import { Rng } from '../sim/rng';
import { generateLevel, GENERATOR_VERSION, paramsForLevel, planGasCans } from './generator';
import { solveArt, type ArtLeg } from './solver/artBot';
import { solveHerd } from './solver/herdBot';
import { solveSoccer } from './solver/soccerBot';
import { solveGreedy } from './solver/greedyPlanner';
import { solveStorm } from './solver/stormBot';

/**
 * Zone bosses (docs/GDD.md §7): set-piece finales unlike normal play, one per zone. Each is
 * generated offline like any campaign lawn and validated by its own bot.
 */
export type BossKind = 'storm' | 'art' | 'sheep' | 'robo' | 'dark' | 'maze' | 'show' | 'meadow';

/** The campaign lawns that are bosses. */
export const BOSS_LEVELS: Readonly<Record<number, BossKind>> = {
  20: 'storm',
  40: 'art',
  60: 'sheep',
  80: 'robo',
  100: 'dark',
  120: 'maze',
  140: 'show',
  160: 'meadow',
};

/**
 * The mowers a boss lawn is built and validated for: one (an endless run's mower), or every mower
 * its bot can drive.
 */
function bossMowers(mower?: MowerId): LevelFile['mowerRule'] {
  return mower ? { kind: 'only', mowers: [mower] } : BOSS_MOWERS;
}

/**
 * Whether a boss lawn can be played with this mower at all (the Great Meadow, open country, takes
 * any mower; the set pieces' bots drive narrow ones only).
 */
export function bossAllows(mower: MowerId, kind?: BossKind): boolean {
  return kind === 'meadow' || !(BOSS_MOWERS.mowers as readonly string[]).includes(mower);
}

/** Generates a boss lawn of the given kind (see each generator). */
export function generateBossLawn(
  kind: BossKind,
  level: number,
  seed: string,
  id: string,
  mower?: MowerId,
): { file: LevelFile; attempts: number } {
  switch (kind) {
    case 'storm':
      return generateStormLawn(level, seed, id, mower);
    case 'art':
      return generateArtLawn(level, seed, id, mower);
    case 'sheep':
      return generateHerdLawn(level, seed, id, mower);
    case 'robo':
      return generateRoboLawn(level, seed, id, mower);
    case 'dark':
      return generateDarkLawn(level, seed, id, mower);
    case 'maze':
      return generateMazeLawn(level, seed, id, mower);
    case 'show':
      return generateSoccerPitch(level, seed, id, mower);
    case 'meadow':
      return generateGreatMeadow(level, seed, id, mower);
  }
}

/**
 * Mower soccer (Stadium, lawn 140; owner 2026-10-05): the pitch's size, goals to win by the
 * whistle, the goals and share mowed for the other stars, the half as the slowest bot's time to
 * score the target times `slack`, and coins about the pitch.
 */
export const SOCCER_PITCH = {
  width: 14,
  height: 18,
  target: 3,
  /** Goals for the second star; the third is mowing this share of what the half has time for. */
  stars: { goals: 5, mowedOfHalf: 0.6 },
  slack: 2,
  cones: 2,
  coins: 6,
} as const;

export interface SoccerOptions {
  /** Goals to win by the whistle. */
  target: number;
  /**
   * The half, as the slowest bot's time to score the target times this (generous: the bot
   * kicks with a precision no thumb has).
   */
  slack: number;
  /** Pairs of training cones, mirrored across the pitch, for the ball to glance off. */
  cones: number;
}

/**
 * Generates a mower soccer pitch: open grass with corner flags and a few training cones (mirrored),
 * a goal mouth in the middle of each end, the ball on the center spot and the mower lined up below
 * it for the kick-off. The half is
 * timed by the soccer bot: long enough for every mower's bot to score the target, plus slack.
 */
export function generateSoccerPitch(
  level: number,
  seed: string,
  id: string,
  mower?: MowerId,
  opts: SoccerOptions = SOCCER_PITCH,
): { file: LevelFile; attempts: number } {
  const { width: w, height: h } = SOCCER_PITCH;
  for (let attempt = 1; attempt <= 20; attempt++) {
    const rng = Rng.fromSeed(`${seed}:soccer:v${GENERATOR_VERSION}:${attempt}`);
    const art: TileId[] = new Array<TileId>(w * h).fill('grass');
    for (const [x, y] of [
      [0, 0],
      [w - 1, 0],
      [0, h - 1],
      [w - 1, h - 1],
    ] as const) {
      art[y * w + x] = 'cornerFlag';
    }
    const center = { x: Math.floor(w / 2), y: Math.floor(h / 2) };
    const start = { x: center.x, y: h - 3 };
    // Training cones in mirrored pairs, clear of the ends, the kick-off line and each other.
    for (let k = 0; k < opts.cones; k++) {
      const x = rng.intBetween(1, center.x - 2);
      const y = rng.intBetween(3, h - 4);
      const near = (cx: number, cy: number) =>
        [-1, 0, 1].some((ox) => [-1, 0, 1].some((oy) => art[(cy + oy) * w + cx + ox] === 'cone'));
      if (near(x, y) || y === center.y) continue;
      art[y * w + x] = 'cone';
      art[y * w + (w - 1 - x)] = 'cone';
    }
    // Coins about the pitch, off the kick-off line and the ends.
    const coinCells = art
      .map((t, i) => ({ t, x: i % w, y: Math.floor(i / w) }))
      .filter((c) => c.t === 'grass' && c.x !== center.x && c.y > 1 && c.y < h - 2);
    const coins = rng
      .shuffle(coinCells)
      .slice(0, SOCCER_PITCH.coins)
      .map((c) => ({ kind: 'coin' as const, x: c.x, y: c.y }));
    const soccer = {
      target: opts.target,
      halfTiles: 100_000,
      stars: { goals: Math.max(SOCCER_PITCH.stars.goals, opts.target + 1), mowed: 1 },
    };
    const base: LevelFile = {
      id,
      name: 'Mower Soccer',
      seed,
      generatorVersion: GENERATOR_VERSION,
      level,
      biome: 'stadium',
      startDir: 'N',
      mowerRule: bossMowers(mower),
      rows: toAsciiRows(w, art, start),
      pickups: coins,
      critters: [],
      soccer,
    };
    // Gas cans along the bot's way for every fueled mower, then time the half.
    const dry = loadLevel(base);
    const mowers = allowedMowers(dry.mowerRule);
    const taken = new Set([
      `${start.x},${start.y}`,
      `${center.x},${center.y}`,
      ...coins.map((c) => `${c.x},${c.y}`),
    ]);
    const cans: { kind: 'gas'; x: number; y: number }[] = [];
    for (const m of mowers) {
      const cap = MOWER_DEFS[m].fuelTiles;
      if (cap === undefined) continue;
      const { route } = solveSoccer(dry.sim, MOWERS[m], {
        ignoreFuel: true,
        stopAtGoals: opts.target + 2,
      });
      for (const c of planGasCans(route, cap, cans, (x, y) => !taken.has(`${x},${y}`))) {
        cans.push({ kind: 'gas', x: c.x, y: c.y });
        taken.add(`${c.x},${c.y}`);
      }
    }
    const fueled = loadLevel({ ...base, pickups: [...coins, ...cans] });
    const scored = mowers.map((m) => {
      const r = solveSoccer(fueled.sim, MOWERS[m], { stopAtGoals: opts.target });
      return r.goals >= opts.target && r.crashes === 0 ? r.ticks / MOWERS[m].ticksPerTile : null;
    });
    if (scored.some((t) => t === null)) continue;
    const halfTiles = Math.ceil(Math.max(...(scored as number[])) * opts.slack);
    // The mowing star: a share of the pitch the half leaves time for, in steps of 5%.
    const grass = art.filter((t) => t === 'grass').length;
    const mowed = Math.floor((20 * SOCCER_PITCH.stars.mowedOfHalf * halfTiles) / grass) / 20;
    const file: LevelFile = {
      ...base,
      pickups: [...coins, ...cans],
      soccer: { ...soccer, halfTiles, stars: { ...soccer.stars, mowed } },
    };
    const def = loadLevel(file);
    const par: Partial<Record<MowerId, number>> = {};
    const ok = mowers.every((m) => {
      const r = solveSoccer(def.sim, MOWERS[m]);
      par[m] = r.ticks;
      return r.won && r.crashes === 0;
    });
    if (ok) return { file: { ...file, par }, attempts: attempt };
  }
  throw new Error(`${id}: no playable soccer pitch after 20 attempts`);
}

/** The Great Meadow: the finale's size (an endless run starts smaller and grows). */
export const GREAT_MEADOW = { width: 24, height: 26 } as const;

/**
 * The Great Meadow (lawn 160, the whole game's finale): one huge meadow to mow in a single go.
 * Rolling hills, a creek with its footbridges, grand trees in flower, regrowing wildflower
 * patches, beehives, a herd of deer and a stag, gas cans spread along the way. A normal lawn for
 * the sim (mow it all); its challenge is its size. Every mower can take it on, the Fairway too.
 */
export function generateGreatMeadow(
  level: number,
  seed: string,
  id: string,
  mower?: MowerId,
  size: { width: number; height: number } = GREAT_MEADOW,
): { file: LevelFile; attempts: number } {
  const base = paramsForLevel(level, { seed, id });
  const { file, attempts } = generateLevel({
    ...base,
    width: size.width,
    height: size.height,
    overgrown: undefined,
    hills: 3,
    bigHills: true,
    bigTrees: 3,
    creek: true,
    critters: 8,
    terrainPatches: base.terrainPatches + 3,
    coins: base.coins + 6,
    rare: true,
    ...(mower ? { mowerRule: { kind: 'only' as const, mowers: [mower] }, required: [mower] } : {}),
  });
  return { file: { ...file, name: 'The Great Meadow' }, attempts };
}

/** A Lawn Art variant: its canvas, world, title, picture and clock. */
export interface ArtLawnOptions {
  width: number;
  height: number;
  biome: 'park';
  name: string;
  /**
   * A recognizable picture on a show lawn, mowed alone (owner, 2026-10-05); or the first art
   * lawns' whole-lawn skyline of bars.
   */
  picture: 'picture' | 'skyline';
  /** For a picture: which ones it may be (one at random); all of them if absent. */
  pictures?: readonly ArtPicture[];
}

/** The county fair's Lawn Art (Park, lawn 40): a flower on the show lawn. */
export const COUNTY_FAIR: ArtLawnOptions = {
  width: 12,
  height: 16,
  biome: 'park',
  name: 'Lawn Art',
  picture: 'picture',
  pictures: ['flower'],
};

/**
 * Hedge Maze: the size in cells (corridor junctions; the lawn is twice that less one), how much
 * longer than the autopilot's run the party waits, and the coins along the paths.
 */
export const MAZE_LAWN = { cellsWide: 10, cellsTall: 12, slack: 1.3, coins: 8 } as const;

/**
 * Hedge Maze (the Grand Estate's finale): a braided maze of tall hedges (every dead end opened
 * up, so any mower can always drive on), its one-tile paths to mow before the garden party
 * starts. Validated with the greedy planner: the mowers it clears cleanly can play it, and the
 * party waits the slowest of their runs, plus some slack. Bigger and tighter with `cells`/`slack`
 * (endless runs).
 */
export function generateMazeLawn(
  level: number,
  seed: string,
  id: string,
  mower?: MowerId,
  size: { cellsWide: number; cellsTall: number; slack: number } = MAZE_LAWN,
): { file: LevelFile; attempts: number } {
  const cw = size.cellsWide;
  const ch = size.cellsTall;
  const w = cw * 2 - 1;
  const h = ch * 2 - 1;
  for (let attempt = 1; attempt <= 30; attempt++) {
    const rng = Rng.fromSeed(`${seed}:maze:v${GENERATOR_VERSION}:${attempt}`);
    const art: TileId[] = new Array<TileId>(w * h).fill('mazeHedge');
    const cell = (cx: number, cy: number) => cy * 2 * w + cx * 2;
    for (let cy = 0; cy < ch; cy++) for (let cx = 0; cx < cw; cx++) art[cell(cx, cy)] = 'grass';
    // A perfect maze (depth-first), then braided: every dead end gets a second way out.
    const seen = new Set<number>();
    const stack: [number, number][] = [[0, ch - 1]];
    seen.add((ch - 1) * cw);
    const open = (ax: number, ay: number, bx: number, by: number) => {
      art[(ay + by) * w + (ax + bx)] = 'grass';
    };
    while (stack.length > 0) {
      const [cx, cy] = stack[stack.length - 1]!;
      const next = rng
        .shuffle(DIRECTIONS.map((d) => [cx + dx(d), cy + dy(d)] as [number, number]))
        .find(([nx, ny]) => nx >= 0 && ny >= 0 && nx < cw && ny < ch && !seen.has(ny * cw + nx));
      if (!next) {
        stack.pop();
        continue;
      }
      seen.add(next[1] * cw + next[0]);
      open(cx, cy, next[0], next[1]);
      stack.push(next);
    }
    const exits = (cx: number, cy: number) =>
      DIRECTIONS.filter((d) => {
        const x = cx * 2 + dx(d);
        const y = cy * 2 + dy(d);
        return x >= 0 && y >= 0 && x < w && y < h && art[y * w + x] === 'grass';
      });
    for (let cy = 0; cy < ch; cy++) {
      for (let cx = 0; cx < cw; cx++) {
        if (exits(cx, cy).length !== 1) continue;
        const walls = rng.shuffle(
          DIRECTIONS.filter((d) => {
            const nx = cx + dx(d);
            const ny = cy + dy(d);
            return nx >= 0 && ny >= 0 && nx < cw && ny < ch && !exits(cx, cy).includes(d);
          }),
        );
        const d = walls[0];
        if (d !== undefined) open(cx, cy, cx + dx(d), cy + dy(d));
      }
    }
    const start = { x: 0, y: h - 1 };
    // Coins along the paths, away from the start.
    const paths = rng.shuffle(
      art.flatMap((t, i) => {
        const x = i % w;
        const y = Math.floor(i / w);
        return t === 'grass' && Math.abs(x - start.x) + Math.abs(y - start.y) > 6 ? [{ x, y }] : [];
      }),
    );
    const pickups = paths.slice(0, MAZE_LAWN.coins).map((p) => ({ kind: 'coin' as const, ...p }));
    const base: LevelFile = {
      id,
      name: 'Hedge Maze',
      seed,
      generatorVersion: GENERATOR_VERSION,
      level,
      biome: 'estate',
      startDir: 'N',
      mowerRule: bossMowers(mower),
      rows: toAsciiRows(w, art, start),
      pickups,
      critters: [],
      party: { tiles: 1_000_000 },
    };
    const def = loadLevel(base);
    // Every mower that clears it cleanly may play; the narrow regulars must.
    const par: Partial<Record<MowerId, number>> = {};
    let longest = 0;
    for (const m of allowedMowers(base.mowerRule)) {
      const r = solveGreedy(def.sim, MOWERS[m], {
        ignoreFuel: true,
        maxTicks: w * h * MOWERS[m].ticksPerTile * 8,
      });
      if (!r.won || r.crashes > 0) continue;
      par[m] = r.ticks;
      longest = Math.max(longest, r.ticks / MOWERS[m].ticksPerTile);
    }
    const must: MowerId[] = mower ? [mower] : ['push', 'selfPropelled', 'zeroTurn'];
    if (must.some((m) => par[m] === undefined)) continue;
    const mowers = Object.keys(par) as MowerId[];
    const tiles = Math.ceil(longest * size.slack);
    // The fuel tank never runs dry on the way: gas cans on the longest route, if it needs them.
    const file: LevelFile = {
      ...base,
      mowerRule: { kind: 'only', mowers },
      party: { tiles },
      par,
    };
    for (const m of mowers) {
      const cap = MOWER_DEFS[m].fuelTiles;
      if (cap === undefined) continue;
      const r = solveGreedy(loadLevel(file).sim, MOWERS[m], {
        ignoreFuel: true,
        maxTicks: w * h * MOWERS[m].ticksPerTile * 8,
      });
      const taken = (x: number, y: number) =>
        file.pickups!.some((p) => p.x === x && p.y === y) || (x === start.x && y === start.y);
      for (const c of planGasCans(
        r.route,
        cap,
        file.pickups!.filter((p) => p.kind === 'gas'),
      )) {
        if (!taken(c.x, c.y)) file.pickups!.push({ kind: 'gas', ...c });
      }
    }
    // Re-time with the cans in place (real tanks).
    const finalPar: Partial<Record<MowerId, number>> = {};
    let ok = true;
    for (const m of mowers) {
      const r = solveGreedy(loadLevel(file).sim, MOWERS[m], {
        maxTicks: w * h * MOWERS[m].ticksPerTile * 8,
      });
      if (!r.won || r.crashes > 0) ok = false;
      else finalPar[m] = r.ticks;
    }
    if (!ok) continue;
    return { file: { ...file, par: finalPar }, attempts: attempt };
  }
  throw new Error(`${id}: no clean maze after 30 attempts`);
}

/** Lights Out: how many old lanterns stand on the lawn. */
export const DARK_LAWN = { lanterns: 7 } as const;

/**
 * Lights Out: a full cemetery lawn at night. Laid out like any other (and validated the same way:
 * the dark is only drawn), with old lanterns spread over it to light. Every mower can play it.
 */
export function generateDarkLawn(
  level: number,
  seed: string,
  id: string,
  mower?: MowerId,
): { file: LevelFile; attempts: number } {
  // No thorns or falling trees: neither would be seen in the dark.
  const base = paramsForLevel(level, { seed, id });
  const { file, attempts } = generateLevel({
    ...base,
    thorns: 0,
    falls: 0,
    lanterns: DARK_LAWN.lanterns,
    ...(mower ? { mowerRule: { kind: 'only' as const, mowers: [mower] }, required: [mower] } : {}),
  });
  return { file: { ...file, name: 'Lights Out', dark: true }, attempts };
}

/** Boss bots drive narrow mowers only: the Fairway Mower (side reels) sits bosses out. */
const BOSS_MOWERS = {
  kind: 'exclude',
  mowers: ['fairway'],
} as const satisfies LevelFile['mowerRule'];

export function bossFor(level: number): BossKind | undefined {
  return BOSS_LEVELS[level];
}

/** Beat the Storm: a long, narrow backyard lawn with the shed at the top. */
export const STORM_LAWN = {
  width: 8,
  height: 48,
  /** Tiles of driving before the rain starts moving. */
  delayTiles: 6,
  /** Share of the grass to mow for the second star. */
  target: 0.7,
  /** Paces tried, easiest (slowest rain) first; the hardest one every mower still beats is used. */
  // Sweeping one row of an 8-wide lawn takes about 9 tiles of driving, so the rain climbing a row
  // every ~8 tiles leaves time to mow most, but not all, of it.
  paces: [10, 9.5, 9, 8.5, 8, 7.5, 7],
  /** Margin over the target the bot must reach, so the star is fair for a person. */
  margin: 0.08,
  coins: 12,
} as const;

const OBSTACLES: readonly TileId[] = ['rock', 'shrub', 'tree', 'birdBath', 'gnome'];

/** Generates the Beat the Storm lawn: obstacles to weave round, a shed to reach, rain to beat. */
export function generateStormLawn(
  level: number,
  seed: string,
  id: string,
  mower?: MowerId,
): { file: LevelFile; attempts: number } {
  const { width: w, height: h } = STORM_LAWN;
  for (let attempt = 1; attempt <= 40; attempt++) {
    const rng = Rng.fromSeed(`${seed}:storm:v${GENERATOR_VERSION}:${attempt}`);
    const art: TileId[] = new Array<TileId>(w * h).fill('grass');
    const at = (x: number, y: number) =>
      x < 0 || y < 0 || x >= w || y >= h ? null : art[y * w + x];
    const start = { x: Math.floor(w / 2), y: h - 1 };
    // Obstacles from row 2 to a few rows above the start: 0-2 per row, never touching another
    // (no walls), so there is always a way up.
    for (let y = 2; y < h - 4; y++) {
      const count = rng.intBetween(0, 2);
      for (let k = 0; k < count; k++) {
        const x = rng.nextInt(w);
        const touches = DIRECTIONS.some((d) => {
          const t = at(x + dx(d), y + dy(d));
          return t !== null && t !== 'grass' && t !== 'flowerBed';
        });
        if (at(x, y) !== 'grass' || touches) continue;
        art[y * w + x] = rng.chance(1, 5) ? 'flowerBed' : rng.pick(OBSTACLES);
      }
    }
    if (!pathToTop(art, w, h, start)) continue;
    const coinCells = art
      .map((t, i) => ({ t, x: i % w, y: Math.floor(i / w) }))
      .filter((c) => c.t === 'grass' && c.y > 0 && c.y < h - 2);
    const pickups = rng
      .shuffle(coinCells)
      .slice(0, STORM_LAWN.coins)
      .map((c) => ({ kind: 'coin' as const, x: c.x, y: c.y }));
    const base: LevelFile = {
      id,
      name: 'Beat the Storm',
      seed,
      generatorVersion: GENERATOR_VERSION,
      level,
      biome: 'backyard',
      startDir: 'N',
      mowerRule: bossMowers(mower),
      rows: toAsciiRows(w, art, start),
      pickups,
      critters: [],
    };
    // The hardest rain every mower still beats (mowing comfortably past the target, except the
    // wide-turning tractor, whose turns rule out a tight zig-zag), with gas cans along the way.
    let chosen: LevelFile | null = null;
    for (const pace of STORM_LAWN.paces) {
      const storm = { delayTiles: STORM_LAWN.delayTiles, pace, target: STORM_LAWN.target };
      const dry = loadLevel({ ...base, storm });
      const mowers = allowedMowers(dry.mowerRule);
      const taken = new Set([`${start.x},${start.y}`, ...pickups.map((p) => `${p.x},${p.y}`)]);
      const cans: { kind: 'gas'; x: number; y: number }[] = [];
      for (const m of mowers) {
        const cap = MOWER_DEFS[m].fuelTiles;
        if (cap === undefined) continue;
        const { route } = solveStorm(dry.sim, MOWERS[m], { ignoreFuel: true });
        for (const c of planGasCans(route, cap, cans, (x, y) => !taken.has(`${x},${y}`))) {
          cans.push({ kind: 'gas', x: c.x, y: c.y });
          taken.add(`${c.x},${c.y}`);
        }
      }
      const file: LevelFile = { ...base, pickups: [...pickups, ...cans], storm };
      const def = loadLevel(file);
      const par: Partial<Record<MowerId, number>> = {};
      const ok = mowers.every((m) => {
        const r = solveStorm(def.sim, MOWERS[m]);
        par[m] = r.ticks;
        const wideTurns = MOWERS[m].turnRule.kind === 'turnSpacing';
        const enough = wideTurns || r.mowedShare >= STORM_LAWN.target + STORM_LAWN.margin;
        return r.won && r.crashes === 0 && enough;
      });
      if (!ok) break;
      chosen = { ...file, par };
    }
    if (!chosen) continue;
    return { file: chosen, attempts: attempt };
  }
  throw new Error(`${id}: no fair storm lawn after 40 attempts`);
}

/** Whether open tiles connect the start to the top row. */
function pathToTop(art: readonly TileId[], w: number, h: number, start: { x: number; y: number }) {
  const blocked = (t: TileId | undefined) => t !== 'grass' && t !== 'flowerBed';
  const seen = new Set<number>([start.y * w + start.x]);
  const queue = [start];
  while (queue.length > 0) {
    const p = queue.shift()!;
    if (p.y === 0) return true;
    for (const d of DIRECTIONS) {
      const x = p.x + dx(d);
      const y = p.y + dy(d);
      const i = y * w + x;
      if (x < 0 || y < 0 || x >= w || y >= h || seen.has(i) || blocked(art[i])) continue;
      seen.add(i);
      queue.push({ x, y });
    }
  }
  return false;
}

/** Lawn Art: the county fair picture. */
export const ART_LAWN = {
  width: 12,
  height: 16,
  /** Picture match for the second and third stars. */
  stars: [0.75, 0.9] as const,
  /** The bot must paint at least this well (all but the wide-turning tractor). */
  botMatch: 0.93,
} as const;

/**
 * Generates the Lawn Art lawn: a clean canvas mowed across inside a frame mowed along (up the two
 * sides), with a skyline of 2-wide bars rising from the bottom to be mowed along, and the judges'
 * table in the bottom-right corner. Validated by
 * painting it with the art bot.
 */
export function generateArtLawn(
  level: number,
  seed: string,
  id: string,
  mower?: MowerId,
  opts: ArtLawnOptions = COUNTY_FAIR,
): { file: LevelFile; attempts: number } {
  if (opts.picture === 'picture') return generatePictureLawn(level, seed, id, mower, opts);
  const { width: w, height: h } = opts;
  for (let attempt = 1; attempt <= 40; attempt++) {
    const rng = Rng.fromSeed(`${seed}:art:v${GENERATOR_VERSION}:${attempt}`);
    const art: TileId[] = new Array<TileId>(w * h).fill('grass');
    const goal = { x: w - 1, y: h - 1 };
    art[goal.y * w + goal.x] = 'judgesTable';
    const start = { x: 0, y: 0 };
    // Skyline bars: 2 wide, gaps of 1-2, never in the table's column.
    const bars: { x: number; top: number }[] = [];
    for (let x = rng.intBetween(1, 2); x + 1 < w - 1; x += 2 + rng.intBetween(1, 2)) {
      bars.push({ x, top: rng.intBetween(3, h - 5) });
    }
    if (bars.length < 3) continue;
    const pattern: string[] = [];
    for (let y = 0; y < h; y++) {
      let row = '';
      for (let x = 0; x < w; x++) {
        if (x === goal.x && y === goal.y) row += '.';
        // A frame up the sides (where rows turn around), the skyline bars, across elsewhere.
        else if (x === 0 || x === w - 1) row += '|';
        else row += bars.some((b) => (x === b.x || x === b.x + 1) && y >= b.top) ? '|' : '-';
      }
      pattern.push(row);
    }
    const legs = artPlan(pattern);
    const base: LevelFile = {
      id,
      name: opts.name,
      seed,
      generatorVersion: GENERATOR_VERSION,
      level,
      biome: opts.biome,
      startDir: 'E',
      mowerRule: bossMowers(mower),
      rows: toAsciiRows(w, art, start),
      pickups: [],
      critters: [],
      art: { pattern, stars: [ART_LAWN.stars[0], ART_LAWN.stars[1]] },
    };
    // Gas cans along the bot's route for every fueled mower, then paint it for real.
    const dry = loadLevel(base);
    const mowers = allowedMowers(dry.mowerRule);
    const taken = new Set([`${start.x},${start.y}`, `${goal.x},${goal.y}`]);
    const cans: { kind: 'gas'; x: number; y: number }[] = [];
    for (const m of mowers) {
      const cap = MOWER_DEFS[m].fuelTiles;
      if (cap === undefined) continue;
      const { route } = solveArt(dry.sim, MOWERS[m], legs, { ignoreFuel: true });
      for (const c of planGasCans(route, cap, cans, (x, y) => !taken.has(`${x},${y}`))) {
        cans.push({ kind: 'gas', x: c.x, y: c.y });
        taken.add(`${c.x},${c.y}`);
      }
    }
    const file: LevelFile = { ...base, pickups: cans };
    const def = loadLevel(file);
    const par: Partial<Record<MowerId, number>> = {};
    const ok = mowers.every((m) => {
      const r = solveArt(def.sim, MOWERS[m], legs);
      par[m] = r.ticks;
      const wideTurns = MOWERS[m].turnRule.kind === 'turnSpacing';
      return r.won && r.crashes === 0 && (wideTurns || r.match >= ART_LAWN.botMatch);
    });
    if (ok) return { file: { ...file, par }, attempts: attempt };
  }
  throw new Error(`${id}: no paintable art lawn after 40 attempts`);
}

/**
 * How the art bot paints a skyline picture (rows '-' across, '|' along): the whole lawn in rows,
 * each 2-wide bar up one column and down the other, then the frame up the left side and down the
 * right onto the judges' table (bottom-right). Derived from the picture, so a baked lawn can be
 * re-checked from its file.
 */
export function artPlan(pattern: readonly string[]): ArtLeg[] {
  const h = pattern.length;
  const w = pattern[0]?.length ?? 0;
  const legs: ArtLeg[] = [];
  for (let y = 0; y < h; y++) {
    legs.push(
      y % 2 === 0 ? { x: 0, y, dir: 1, length: w - 1 } : { x: w - 1, y, dir: 3, length: w - 1 },
    );
  }
  const along = (x: number, y: number) => pattern[y]?.[x] === '|';
  for (let x = 1; x + 1 < w - 1; x++) {
    if (!along(x, h - 1) || !along(x + 1, h - 1)) continue;
    let top = h - 1;
    while (top > 0 && along(x, top - 1)) top--;
    legs.push({ x, y: h - 1, dir: 0, length: h - 1 - top });
    legs.push({ x: x + 1, y: top, dir: 2, length: h - 1 - top });
    x++; // the bar's second column
  }
  legs.push({ x: 0, y: h - 1, dir: 0, length: h - 1 });
  legs.push({ x: w - 1, y: 0, dir: 2, length: h - 1 });
  return legs;
}

/**
 * Generates a picture Lawn Art lawn (owner, 2026-10-05): a recognizable picture (artPictures.ts)
 * centered on a show lawn that is already perfect; only the picture is grass, so only it is
 * mowed, and the lawn ends as the last of it is cut. Validated by painting it with the art bot.
 */
function generatePictureLawn(
  level: number,
  seed: string,
  id: string,
  mower: MowerId | undefined,
  opts: ArtLawnOptions,
): { file: LevelFile; attempts: number } {
  const { width: w, height: h } = opts;
  const names = opts.pictures ?? ART_PICTURE_NAMES;
  for (let attempt = 1; attempt <= 20; attempt++) {
    const rng = Rng.fromSeed(`${seed}:picture:v${GENERATOR_VERSION}:${attempt}`);
    const picture = rng.pick(names);
    const pattern = pictureOnCanvas(picture, w, h);
    const art: TileId[] = pattern.flatMap((row) =>
      [...row].map((c): TileId => (c === '.' ? 'showLawn' : 'grass')),
    );
    const start = { x: 0, y: h - 1 };
    const legs = picturePlan(pattern);
    const base: LevelFile = {
      id,
      name: opts.name,
      seed,
      generatorVersion: GENERATOR_VERSION,
      level,
      biome: opts.biome,
      startDir: 'N',
      mowerRule: bossMowers(mower),
      rows: toAsciiRows(w, art, start),
      pickups: [],
      critters: [],
      art: { pattern, stars: [ART_LAWN.stars[0], ART_LAWN.stars[1]] },
    };
    // Gas cans along the bot's route for every fueled mower, then paint it for real.
    const dry = loadLevel(base);
    const mowers = allowedMowers(dry.mowerRule);
    const taken = new Set([`${start.x},${start.y}`]);
    const cans: { kind: 'gas'; x: number; y: number }[] = [];
    for (const m of mowers) {
      const cap = MOWER_DEFS[m].fuelTiles;
      if (cap === undefined) continue;
      const { route } = solveArt(dry.sim, MOWERS[m], legs, { ignoreFuel: true });
      for (const c of planGasCans(route, cap, cans, (x, y) => !taken.has(`${x},${y}`))) {
        cans.push({ kind: 'gas', x: c.x, y: c.y });
        taken.add(`${c.x},${c.y}`);
      }
    }
    const file: LevelFile = { ...base, pickups: cans };
    const def = loadLevel(file);
    const par: Partial<Record<MowerId, number>> = {};
    const ok = mowers.every((m) => {
      const r = solveArt(def.sim, MOWERS[m], legs);
      par[m] = r.ticks;
      const wideTurns = MOWERS[m].turnRule.kind === 'turnSpacing';
      return r.won && r.crashes === 0 && (wideTurns || r.match >= ART_LAWN.botMatch);
    });
    if (ok) return { file: { ...file, par }, attempts: attempt };
  }
  throw new Error(`${id}: no paintable picture lawn after 20 attempts`);
}

/**
 * How the art bot paints a picture: the dark (across) stretches first, row by row, then the light
 * (along) ones, column by column, each entered straight from the show lawn where it can be (a dark
 * stretch shut in by light is entered through it, and the light pass puts that right). The bot
 * finds its way between them round the picture.
 */
export function picturePlan(pattern: readonly string[]): ArtLeg[] {
  const h = pattern.length;
  const w = pattern[0]?.length ?? 0;
  const at = (x: number, y: number) =>
    x < 0 || y < 0 || x >= w || y >= h ? '#' : (pattern[y]?.[x] ?? '#');
  const legs: ArtLeg[] = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (at(x, y) !== '-' || at(x - 1, y) === '-') continue;
      let end = x;
      while (at(end + 1, y) === '-') end++;
      const length = end - x + 1;
      // From the show lawn if either end has it, else through the light beside it.
      if (at(x - 1, y) === '.' || (at(end + 1, y) !== '.' && x > 0)) {
        legs.push({ x: x - 1, y, dir: 1, length });
      } else {
        legs.push({ x: end + 1, y, dir: 3, length });
      }
      x = end;
    }
  }
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) {
      if (at(x, y) !== '|' || at(x, y - 1) === '|') continue;
      let end = y;
      while (at(x, end + 1) === '|') end++;
      const length = end - y + 1;
      if (at(x, y - 1) === '.') legs.push({ x, y: y - 1, dir: 2, length });
      else legs.push({ x, y: end + 1, dir: 0, length });
      y = end;
    }
  }
  return legs;
}

/** The art bot's plan for any art lawn: a picture's, or the first lawns' (with a judges' table). */
export function artLegs(art: ArtSpec, width: number): ArtLeg[] {
  const rows = artRows(art.pattern, width);
  return art.goal ? artPlan(rows) : picturePlan(rows);
}

/** A lawn-art pattern (as the sim holds it) back to rows: '-' across, '|' along, '.' none. */
export function artRows(pattern: readonly number[], width: number): string[] {
  const rows: string[] = [];
  for (let i = 0; i < pattern.length; i += width) {
    rows.push(
      pattern
        .slice(i, i + width)
        .map((a) => (a === 1 ? '-' : a === 2 ? '|' : '.'))
        .join(''),
    );
  }
  return rows;
}

/** Round Up the Sheep: a farm paddock with the pen at the top. */
export const HERD_LAWN = {
  width: 12,
  height: 16,
  sheep: 4,
  coins: 8,
  /** The pen: this many tiles wide and deep, centered at the top, the gate below its middle. */
  penWidth: 4,
  penDepth: 2,
} as const;

const FARM_OBSTACLES: readonly TileId[] = ['hayBale', 'trough', 'rock'];

/**
 * Generates the Round Up the Sheep lawn: a paddock with a few hay bales and troughs, the fenced pen
 * at the top with its gate, and sheep grazing in the lower half. Validated by herding and mowing
 * it with the herd bot, with every mower.
 */
export function generateHerdLawn(
  level: number,
  seed: string,
  id: string,
  mower?: MowerId,
  /** How many sheep (default {@link HERD_LAWN.sheep}; at most 8, the pen's size). */
  sheepCount: number = HERD_LAWN.sheep,
): { file: LevelFile; attempts: number } {
  const { width: w, height: h, penWidth, penDepth } = HERD_LAWN;
  for (let attempt = 1; attempt <= 60; attempt++) {
    const rng = Rng.fromSeed(`${seed}:herd:v${GENERATOR_VERSION}:${attempt}`);
    const art: TileId[] = new Array<TileId>(w * h).fill('grass');
    const at = (x: number, y: number) =>
      x < 0 || y < 0 || x >= w || y >= h ? null : art[y * w + x];
    const penX = Math.floor((w - penWidth) / 2);
    for (let y = 0; y < penDepth; y++) {
      for (let x = penX; x < penX + penWidth; x++) art[y * w + x] = 'pen';
    }
    const gate = { x: penX + Math.floor(penWidth / 2) - (rng.chance(1, 2) ? 1 : 0), y: penDepth };
    art[gate.y * w + gate.x] = 'penGate';
    const start = { x: rng.chance(1, 2) ? 0 : w - 1, y: h - 1 };
    // Farm props: never touching another, the pen or the gate's approach, nor the start.
    const keepClear = (x: number, y: number) =>
      Math.abs(x - gate.x) + Math.abs(y - gate.y) <= 3 ||
      Math.abs(x - start.x) + Math.abs(y - start.y) <= 2;
    for (let k = rng.intBetween(4, 6); k > 0; k--) {
      const x = rng.nextInt(w);
      const y = rng.intBetween(penDepth + 1, h - 2);
      const touches = [...DIRECTIONS.map((d) => at(x + dx(d), y + dy(d))), at(x, y)].some(
        (t) => t !== null && t !== 'grass',
      );
      if (touches || keepClear(x, y)) continue;
      art[y * w + x] = rng.pick(FARM_OBSTACLES);
    }
    // Sheep in the lower half, on grass with room round them, apart and off the start.
    const open = (x: number, y: number) => at(x, y) === 'grass';
    const sheep: { kind: 'sheep'; x: number; y: number }[] = [];
    for (let tries = 0; sheep.length < sheepCount && tries < 300; tries++) {
      const x = rng.intBetween(1, w - 2);
      const y = rng.intBetween(Math.floor(h / 2), h - 3);
      const roomy = DIRECTIONS.every((d) => open(x + dx(d), y + dy(d)));
      const apart = sheep.every((c) => Math.abs(c.x - x) + Math.abs(c.y - y) >= 3);
      const offStart = Math.abs(x - start.x) + Math.abs(y - start.y) >= 3;
      if (open(x, y) && roomy && apart && offStart) sheep.push({ kind: 'sheep', x, y });
    }
    if (sheep.length < sheepCount) continue;
    const taken = new Set([`${start.x},${start.y}`, ...sheep.map((c) => `${c.x},${c.y}`)]);
    const coinCells = art
      .map((t, i) => ({ t, x: i % w, y: Math.floor(i / w) }))
      .filter((c) => c.t === 'grass' && !taken.has(`${c.x},${c.y}`));
    const coins = rng
      .shuffle(coinCells)
      .slice(0, HERD_LAWN.coins)
      .map((c) => ({ kind: 'coin' as const, x: c.x, y: c.y }));
    for (const c of coins) taken.add(`${c.x},${c.y}`);
    const base: LevelFile = {
      id,
      name: 'Round Up the Sheep',
      seed,
      generatorVersion: GENERATOR_VERSION,
      level,
      biome: 'farm',
      startDir: 'N',
      mowerRule: bossMowers(mower),
      rows: toAsciiRows(w, art, start),
      pickups: coins,
      critters: sheep,
      herd: true,
    };
    // Gas cans along the bot's route for every fueled mower, then herd it for real.
    const dry = loadLevel(base);
    const mowers = allowedMowers(dry.mowerRule);
    const cans: { kind: 'gas'; x: number; y: number }[] = [];
    let stuck = false;
    for (const m of mowers) {
      const cap = MOWER_DEFS[m].fuelTiles;
      if (cap === undefined) continue;
      const r = solveHerd(dry.sim, MOWERS[m], { ignoreFuel: true });
      if (!r.won) {
        stuck = true;
        break;
      }
      for (const c of planGasCans(r.route, cap, cans, (x, y) => !taken.has(`${x},${y}`))) {
        cans.push({ kind: 'gas', x: c.x, y: c.y });
        taken.add(`${c.x},${c.y}`);
      }
    }
    if (stuck) continue;
    const file: LevelFile = { ...base, pickups: [...coins, ...cans] };
    const def = loadLevel(file);
    const par: Partial<Record<MowerId, number>> = {};
    const ok = mowers.every((m) => {
      const r = solveHerd(def.sim, MOWERS[m]);
      par[m] = r.ticks;
      return r.won && r.crashes === 0;
    });
    if (!ok) continue;
    return { file: { ...file, par }, attempts: attempt };
  }
  throw new Error(`${id}: no herdable sheep lawn after 60 attempts`);
}

/** Rival Robo-Mower: a golf-course fairway, the robot docked in the far corner. */
export const ROBO_LAWN = {
  width: 12,
  height: 16,
  coins: 8,
  /** Share of the grass to mow for the second star. */
  target: 0.6,
  /** The robot's speeds tried (percent of the player's mower), easiest first. */
  speeds: [50, 55, 60, 65, 70, 75, 80, 85, 90],
  /** Margin over the target the bot must reach, so the star is fair for a person. */
  margin: 0.03,
} as const;

const GOLF_OBSTACLES: readonly TileId[] = ['tree', 'tree', 'rock', 'shrub'];

/**
 * Generates the Rival Robo-Mower lawn: a fairway with a sand bunker, a pond and a few trees, the
 * player starting in one bottom corner and the robot's dock in the opposite top corner. The robot
 * is as fast as it can be while the greedy planner still out-mows it, comfortably past the
 * target, with every mower.
 */
export function generateRoboLawn(
  level: number,
  seed: string,
  id: string,
  mower?: MowerId,
  /** A set robot speed (endless runs), instead of the fastest one the bot still beats. */
  robotSpeed?: number,
): { file: LevelFile; attempts: number } {
  const { width: w, height: h } = ROBO_LAWN;
  for (let attempt = 1; attempt <= 40; attempt++) {
    const rng = Rng.fromSeed(`${seed}:robo:v${GENERATOR_VERSION}:${attempt}`);
    const art: TileId[] = new Array<TileId>(w * h).fill('grass');
    const at = (x: number, y: number) =>
      x < 0 || y < 0 || x >= w || y >= h ? null : art[y * w + x];
    const left = rng.chance(1, 2);
    const start = { x: left ? 0 : w - 1, y: h - 1 };
    const dock = { x: left ? w - 1 : 0, y: 0 };
    art[dock.y * w + dock.x] = 'roboDock';
    const nearEnds = (x: number, y: number) =>
      Math.abs(x - start.x) + Math.abs(y - start.y) <= 3 ||
      Math.abs(x - dock.x) + Math.abs(y - dock.y) <= 3;
    // A sand bunker (open ground) and a pond (in the way), as blobs away from both ends.
    for (const [tile, size] of [
      ['sand', rng.intBetween(3, 5)],
      ['water', rng.intBetween(2, 4)],
    ] as const) {
      let x = rng.intBetween(2, w - 3);
      let y = rng.intBetween(3, h - 4);
      for (let k = 0; k < size * 3 && size > 0; k++) {
        if (at(x, y) === 'grass' && !nearEnds(x, y)) art[y * w + x] = tile;
        const d = rng.pick(DIRECTIONS);
        if (at(x + dx(d), y + dy(d)) !== null) {
          x += dx(d);
          y += dy(d);
        }
      }
    }
    for (let k = rng.intBetween(3, 5); k > 0; k--) {
      const x = rng.nextInt(w);
      const y = rng.intBetween(1, h - 2);
      const touches = [...DIRECTIONS.map((d) => at(x + dx(d), y + dy(d))), at(x, y)].some(
        (t) => t !== null && t !== 'grass',
      );
      if (!touches && !nearEnds(x, y)) art[y * w + x] = rng.pick(GOLF_OBSTACLES);
    }
    if (
      !pathToTop(
        art.map((t) => (t === 'sand' || t === 'roboDock' ? 'grass' : t)),
        w,
        h,
        start,
      )
    )
      continue;
    const taken = new Set([`${start.x},${start.y}`, `${dock.x},${dock.y}`]);
    const coins = rng
      .shuffle(
        art
          .map((t, i) => ({ t, x: i % w, y: Math.floor(i / w) }))
          .filter((c) => c.t === 'grass' && !taken.has(`${c.x},${c.y}`)),
      )
      .slice(0, ROBO_LAWN.coins)
      .map((c) => ({ kind: 'coin' as const, x: c.x, y: c.y }));
    for (const c of coins) taken.add(`${c.x},${c.y}`);
    const base: LevelFile = {
      id,
      name: 'Rival Robo-Mower',
      seed,
      generatorVersion: GENERATOR_VERSION,
      level,
      biome: 'golf',
      startDir: 'N',
      mowerRule: bossMowers(mower),
      rows: toAsciiRows(w, art, start),
      pickups: coins,
      critters: [],
    };
    let chosen: LevelFile | null = null;
    for (const speed of robotSpeed !== undefined ? [robotSpeed] : ROBO_LAWN.speeds) {
      const rival = {
        dir: left ? ('W' as const) : ('E' as const),
        speed,
        target: ROBO_LAWN.target,
      };
      const dry = loadLevel({ ...base, rival });
      const mowers = allowedMowers(dry.mowerRule);
      const placed = new Set(taken);
      const cans: { kind: 'gas'; x: number; y: number }[] = [];
      for (const m of mowers) {
        const cap = MOWER_DEFS[m].fuelTiles;
        if (cap === undefined) continue;
        const { route } = solveGreedy(dry.sim, MOWERS[m], { ignoreFuel: true });
        for (const c of planGasCans(route, cap, cans, (x, y) => !placed.has(`${x},${y}`))) {
          cans.push({ kind: 'gas', x: c.x, y: c.y });
          placed.add(`${c.x},${c.y}`);
        }
      }
      const file: LevelFile = { ...base, pickups: [...coins, ...cans], rival };
      const def = loadLevel(file);
      const par: Partial<Record<MowerId, number>> = {};
      const ok = mowers.every((m) => {
        const r = solveGreedy(def.sim, MOWERS[m]);
        par[m] = r.ticks;
        const score = r.sim.rivalScore()!;
        const share = score.you / Math.max(1, r.sim.grid.totalGrass);
        const wideTurns = MOWERS[m].turnRule.kind === 'turnSpacing';
        return (
          r.won && r.crashes === 0 && (wideTurns || share >= ROBO_LAWN.target + ROBO_LAWN.margin)
        );
      });
      if (!ok && robotSpeed === undefined) break;
      chosen = { ...file, par };
    }
    if (!chosen) continue;
    return { file: chosen, attempts: attempt };
  }
  throw new Error(`${id}: no fair robo lawn after 40 attempts`);
}

/** Rain Chase (endless): a long storm lawn whose rain speeds up as it climbs. */
export const RAIN_CHASE = {
  width: 8,
  height: 240,
  /** The rain starts at a gentle pace and climbs a little faster every row, down to `minPace`. */
  pace: 11,
  accel: 0.03,
  minPace: 5,
  delayTiles: 8,
  /** Gas cans every this many rows (the tank of every mower lasts much longer). */
  gasEvery: 14,
  coinsPerRow: 0.3,
} as const;

/**
 * Generates a Rain Chase lawn: a long, narrow lawn with obstacles to weave round (never sealing
 * the way up), coins and gas cans along it, and rain that keeps coming faster. Not validated with
 * a bot: it is meant to catch everyone eventually; the score is how far you get.
 */
export function generateRainChase(seed: string, id: string, mower: MowerId): LevelFile {
  const { width: w, height: h } = RAIN_CHASE;
  for (let attempt = 1; ; attempt++) {
    const rng = Rng.fromSeed(`${seed}:rain:v${GENERATOR_VERSION}:${attempt}`);
    const art: TileId[] = new Array<TileId>(w * h).fill('grass');
    const at = (x: number, y: number) =>
      x < 0 || y < 0 || x >= w || y >= h ? null : art[y * w + x];
    const start = { x: Math.floor(w / 2), y: h - 1 };
    for (let y = 2; y < h - 4; y++) {
      // Busier the higher you get.
      const count = rng.intBetween(0, y < h / 2 ? 3 : 2);
      for (let k = 0; k < count; k++) {
        const x = rng.nextInt(w);
        const touches = DIRECTIONS.some((d) => {
          const t = at(x + dx(d), y + dy(d));
          return t !== null && t !== 'grass' && t !== 'flowerBed';
        });
        if (at(x, y) !== 'grass' || touches) continue;
        art[y * w + x] = rng.chance(1, 5) ? 'flowerBed' : rng.pick(OBSTACLES);
      }
    }
    if (!pathToTop(art, w, h, start) && attempt < 20) continue;
    const pickups: { kind: 'coin' | 'gas'; x: number; y: number }[] = [];
    const open = (x: number, y: number) =>
      at(x, y) === 'grass' && !(x === start.x && y === start.y);
    for (let y = h - 3; y > 0; y--) {
      const x = rng.nextInt(w);
      if (!open(x, y)) continue;
      if ((h - y) % RAIN_CHASE.gasEvery === 0 && MOWER_DEFS[mower].fuelTiles !== undefined) {
        pickups.push({ kind: 'gas', x, y });
      } else if (rng.nextInt(100) < RAIN_CHASE.coinsPerRow * 100) {
        pickups.push({ kind: 'coin', x, y });
      }
    }
    return {
      id,
      name: 'Rain Chase',
      seed,
      generatorVersion: GENERATOR_VERSION,
      biome: 'backyard',
      startDir: 'N',
      mowerRule: { kind: 'only', mowers: [mower] },
      rows: toAsciiRows(w, art, start),
      pickups,
      critters: [],
      storm: {
        delayTiles: RAIN_CHASE.delayTiles,
        pace: RAIN_CHASE.pace,
        target: 1,
        accel: RAIN_CHASE.accel,
        minPace: RAIN_CHASE.minPace,
      },
    };
  }
}
